// Display formatters. None of these are trusted for billing — the BFF is the
// source of truth for money. These only render numbers the server returned (or
// a clearly-labelled client-side "estimate").

/** USD from a plain dollar amount, e.g. 2.5 -> "$2.50". */
export function formatUsd(amount: number, opts: { cents?: boolean } = {}): string {
  const value = opts.cents ? amount / 100 : amount;
  if (!Number.isFinite(value)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

/** USD from an integer cents value, e.g. 250 -> "$2.50". */
export function formatCents(cents: number): string {
  return formatUsd(cents, { cents: true });
}

/** Millions of OSRS gold, e.g. 1500 -> "1.5B", 12 -> "12M". */
export function formatGoldM(millions: number): string {
  if (!Number.isFinite(millions)) return '0M';
  if (millions >= 1000) {
    const b = millions / 1000;
    return `${trimZeros(b.toFixed(2))}B`;
  }
  return `${trimZeros(millions.toFixed(2))}M`;
}

/** Thousands separators for large integers (XP, GP). */
export function formatInt(n: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(n));
}

function trimZeros(s: string): string {
  return s.replace(/\.?0+$/, '');
}

/** Convert a catalog "/pics/..." path (possibly URL-encoded) to a usable src. */
export function picUrl(ref: string | null | undefined): string {
  if (!ref) return '';
  if (/^https?:\/\//i.test(ref)) return ref;
  // Catalog references are already root-relative ("/pics/...") and assets are
  // migrated into public/pics with identical paths, so they resolve as-is.
  return ref;
}

/** Relative time, e.g. "3 minutes ago". */
export function relativeTime(input: string | number | Date): string {
  const then = new Date(input).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);
  const table: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [3600, 'minute'],
    [86400, 'hour'],
    [604800, 'day'],
    [2629800, 'week'],
    [31557600, 'month'],
    [Infinity, 'year'],
  ];
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  let divisor = 1;
  for (const [limit, unit] of table) {
    if (Math.abs(seconds) < limit) {
      const value = Math.round(seconds / divisor);
      return rtf.format(-value, unit);
    }
    divisor = limit;
  }
  return 'just now';
}
